import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AppNavBar from '../layout/AppNavBar';
import BottomTabBar from '../layout/BottomTabBar';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';

const TYPE_META = {
  resume:      { label: 'Resume',       color: '#2E6AFF', bg: 'rgba(46,106,255,0.12)' },
  coverletter: { label: 'Cover Letter', color: '#00D4A0', bg: 'rgba(0,212,160,0.12)' },
  cv:          { label: 'CV',           color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  bio:         { label: 'Bio',          color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
  linkedin:    { label: 'LinkedIn',     color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  portfolio:   { label: 'Portfolio',    color: '#ec4899', bg: 'rgba(236,72,153,0.12)' },
};

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'type',   label: 'By Type' },
  { value: 'score',  label: 'By ATS Score' },
];

const FILTER_TYPES = ['all', 'resume', 'coverletter', 'cv', 'bio', 'linkedin', 'portfolio'];

function TypeBadge({ type }) {
  const meta = TYPE_META[(type || '').toLowerCase()] || { label: type, color: '#6B7FA8', bg: 'rgba(107,127,168,0.12)' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '99px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      background: meta.bg,
      color: meta.color,
    }}>
      {meta.label}
    </span>
  );
}

function ScoreBadge({ score }) {
  if (score == null) return null;
  const s = Math.round(score);
  const color = s >= 90 ? '#10B981' : s >= 80 ? '#F59E0B' : '#EF4444';
  return (
    <span style={{
      fontSize: '12px',
      fontWeight: 700,
      color,
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      {s}%
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function StatCard({ label, value, color }) {
  return (
    <div style={s.statCard}>
      <div style={{ ...s.statValue, color: color || '#E8EEFF' }}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

export default function DocumentsDashboard() {
  const session = useSelector((state) => state.auth.session);
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => { fetchDocuments(); }, []); // eslint-disable-line

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/documents`, {
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load documents');
      setDocuments(data.documents);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (bulkDeleting) return;
    const ids = [...selectedIds];
    setBulkDeleting(true);
    setSelectedIds(new Set());
    await Promise.all(ids.map(id =>
      fetch(`${API_BASE_URL}/api/documents/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      }).catch(() => {})
    ));
    setDocuments(prev => prev.filter(d => !ids.includes(d.id)));
    setBulkDeleting(false);
  };

  const buildResult = (doc) => {
    const scores = doc.scores || {};
    const atsComposite = scores.ats_composite || {};
    const humanComposite = scores.human_composite || {};
    const originalScores = scores.original_scores || null;
    return {
      rawOptimizedContent: '',
      description: '',
      resumeText: doc.content,
      scores,
      atsComposite,
      humanComposite,
      atsOverall: atsComposite.overall_score ?? scores.atsScore ?? null,
      atsCategories: atsComposite.categories || [],
      humanOverall: humanComposite.overall_score ?? scores.humanScore ?? null,
      humanCategories: humanComposite.categories || [],
      fixPanel: [],
      originalResumeText: doc.content,
      originalScores,
      docType: doc.type,
      metadata: {},
    };
  };

  const handleLoad = async (docId) => {
    setLoadingId(docId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/documents/${docId}`, {
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load document');

      const doc = data.document;
      navigate('/create', {
        state: {
          loadedResults: [buildResult(doc)],
          applicationInfo: {
            targetJobTitle: doc.job_title || '',
            company: doc.company || '',
          },
        },
      });
    } catch (err) {
      alert(`Failed to load document: ${err.message}`);
    } finally {
      setLoadingId(null);
    }
  };

  const handleLoadSelected = async () => {
    if (bulkLoading) return;
    const ids = [...selectedIds];
    setBulkLoading(true);
    try {
      const results = await Promise.all(ids.map(async (docId) => {
        const res = await fetch(`${API_BASE_URL}/api/documents/${docId}`, {
          headers: { 'Authorization': `Bearer ${session?.access_token}` },
        });
        const data = await res.json();
        return data.success ? buildResult(data.document) : null;
      }));
      const valid = results.filter(Boolean);
      if (valid.length === 0) return;
      const firstDoc = documents.find(d => d.id === ids[0]);
      setSelectedIds(new Set());
      navigate('/create', {
        state: {
          loadedResults: valid,
          applicationInfo: {
            targetJobTitle: firstDoc?.job_title || '',
            company: firstDoc?.company || '',
          },
        },
      });
    } catch (err) {
      alert(`Failed to load documents: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDeleteConfirm = async (docId) => {
    setDeletingId(docId);
    setConfirmDeleteId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to delete');
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      alert(`Failed to delete document: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const stats = useMemo(() => {
    const counts = { total: documents.length };
    FILTER_TYPES.slice(1).forEach(t => {
      counts[t] = documents.filter(d => (d.type || '').toLowerCase() === t).length;
    });
    return counts;
  }, [documents]);

  const filtered = useMemo(() => {
    let list = documents;
    if (filterType !== 'all') list = list.filter(d => (d.type || '').toLowerCase() === filterType);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        (d.job_title || '').toLowerCase().includes(q) ||
        (d.company || '').toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'type') return (a.type || '').localeCompare(b.type || '');
      if (sortBy === 'score') {
        const sa = a.scores?.ats_composite?.overall_score ?? a.scores?.atsScore ?? 0;
        const sb = b.scores?.ats_composite?.overall_score ?? b.scores?.atsScore ?? 0;
        return sb - sa;
      }
      return 0;
    });
    return list;
  }, [documents, filterType, search, sortBy]);

  return (
    <div style={s.page}>
      <AppNavBar pageTitle="My Documents" />
      <BottomTabBar />

      <div style={s.container}>
        {/* ── Stats Row ── */}
        {!loading && !error && documents.length > 0 && (
          <div style={s.statsRow}>
            <StatCard label="Total" value={stats.total} color="#E8EEFF" />
            {FILTER_TYPES.slice(1).filter(t => stats[t] > 0).map(t => (
              <StatCard
                key={t}
                label={TYPE_META[t]?.label || t}
                value={stats[t]}
                color={TYPE_META[t]?.color}
              />
            ))}
          </div>
        )}

        {/* ── Controls ── */}
        {!loading && !error && documents.length > 0 && (
          <div style={s.controls}>
            {/* Filter pills */}
            <div style={s.filterPills}>
              {FILTER_TYPES.map(t => (
                <button
                  key={t}
                  style={{
                    ...s.filterPill,
                    ...(filterType === t ? s.filterPillActive : {}),
                    ...(t !== 'all' && filterType === t ? { color: TYPE_META[t]?.color, borderColor: TYPE_META[t]?.color, background: TYPE_META[t]?.bg } : {}),
                  }}
                  onClick={() => setFilterType(t)}
                >
                  {t === 'all' ? 'All' : (TYPE_META[t]?.label || t)}
                  {t !== 'all' && stats[t] > 0 && (
                    <span style={s.filterPillCount}>{stats[t]}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Search + Sort row */}
            <div style={s.searchRow}>
              <div style={s.searchWrap}>
                <svg style={s.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  style={s.searchInput}
                  type="text"
                  placeholder="Search by job title or company…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button style={s.searchClear} onClick={() => setSearch('')}>×</button>
                )}
              </div>
              <select
                style={s.sortSelect}
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── States ── */}
        {loading && (
          <div style={s.center}>
            <div style={s.spinner} />
            <span style={s.dimText}>Loading your documents…</span>
          </div>
        )}

        {!loading && error && (
          <div style={s.errorBox}>
            <div style={s.errorTitle}>Failed to load documents</div>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>{error}</div>
            <button style={s.retryBtn} onClick={fetchDocuments}>Try Again</button>
          </div>
        )}

        {!loading && !error && documents.length === 0 && (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#1E2D4A' }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <p style={s.emptyTitle}>No documents yet</p>
            <p style={s.dimText}>Generate your first resume, cover letter, CV, or LinkedIn update — documents are saved automatically.</p>
            <button onClick={() => navigate('/create')} style={s.primaryBtn}>
              Generate Documents →
            </button>
          </div>
        )}

        {!loading && !error && documents.length > 0 && filtered.length === 0 && (
          <div style={s.center}>
            <p style={s.emptyTitle}>No results</p>
            <p style={s.dimText}>Try clearing the search or changing the filter.</p>
            <button style={s.ghostBtn} onClick={() => { setSearch(''); setFilterType('all'); }}>
              Clear filters
            </button>
          </div>
        )}

        {/* ── Document Grid ── */}
        {!loading && !error && filtered.length > 0 && (
          <div style={s.grid}>
            {filtered.map((doc) => {
              const atsScore =
                doc.scores?.ats_composite?.overall_score ??
                doc.scores?.atsScore ??
                null;
              const meta = TYPE_META[(doc.type || '').toLowerCase()] || {};
              const isDeleting = deletingId === doc.id;
              const isLoading = loadingId === doc.id;
              const isSelected = selectedIds.has(doc.id);

              return (
                <div
                  key={doc.id}
                  style={{
                    ...s.card,
                    borderColor: isSelected ? '#2E6AFF' : isLoading ? (meta.color || '#1E2D4A') : '#1E2D4A',
                    opacity: isDeleting ? 0.5 : 1,
                    boxShadow: isSelected ? '0 0 0 1px rgba(46,106,255,0.3)' : 'none',
                  }}
                >
                  {/* Card top row */}
                  <div style={s.cardTop}>
                    <TypeBadge type={doc.type} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {atsScore != null && (
                        <div style={s.scoreWrap}>
                          <span style={s.scoreLabel}>ATS</span>
                          <ScoreBadge score={atsScore} />
                        </div>
                      )}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(doc.id)}
                        style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#2E6AFF', flexShrink: 0 }}
                        title="Select document"
                      />
                    </div>
                  </div>

                  {/* Card body */}
                  <div style={s.cardBody}>
                    <p style={s.jobTitle}>
                      {doc.job_title || <span style={{ color: '#4E6385', fontStyle: 'italic' }}>No job title</span>}
                    </p>
                    {doc.company && (
                      <p style={s.company}>{doc.company}</p>
                    )}
                  </div>

                  {/* Card footer */}
                  <div style={s.cardFooter}>
                    <span style={s.date}>{formatDate(doc.created_at)}</span>

                    <div style={s.actions}>
                      {confirmDeleteId === doc.id ? (
                        <>
                          <span style={s.confirmText}>Delete?</span>
                          <button onClick={() => handleDeleteConfirm(doc.id)} style={s.confirmYes} disabled={isDeleting}>
                            Yes
                          </button>
                          <button onClick={() => setConfirmDeleteId(null)} style={s.confirmNo}>
                            No
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleLoad(doc.id)}
                            style={s.loadBtn}
                            disabled={isLoading || isDeleting}
                          >
                            {isLoading ? (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                                Loading…
                              </span>
                            ) : 'View'}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(doc.id)}
                            style={s.deleteBtn}
                            disabled={isDeleting || isLoading}
                            title="Delete document"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6M14 11v6" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Bulk Action Bar ── */}
      {selectedIds.size > 0 && (
        <div style={s.bulkBar}>
          <span style={s.bulkCount}>{selectedIds.size} selected</span>
          <button
            onClick={() => setSelectedIds(new Set())}
            style={s.bulkClear}
          >
            Clear
          </button>
          <button
            onClick={handleLoadSelected}
            disabled={bulkLoading || bulkDeleting}
            style={s.bulkView}
          >
            {bulkLoading ? 'Loading…' : `View ${selectedIds.size}`}
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting || bulkLoading}
            style={s.bulkDelete}
          >
            {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Styles ── */
const s = {
  page: {
    minHeight: '100vh',
    background: '#0A0F1E',
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    color: '#E8EEFF',
  },
  bulkBar: {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#0F1629',
    border: '1px solid #2E6AFF',
    borderRadius: '40px',
    padding: '10px 20px',
    zIndex: 100,
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
  },
  bulkCount: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#E8EEFF',
    marginRight: '4px',
  },
  bulkClear: {
    padding: '5px 14px',
    background: 'transparent',
    border: '1px solid #1E2D4A',
    borderRadius: '20px',
    color: '#6B7FA8',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  bulkView: {
    padding: '5px 14px',
    background: 'rgba(46,106,255,0.12)',
    border: '1px solid rgba(46,106,255,0.3)',
    borderRadius: '20px',
    color: '#7EB4FF',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  bulkDelete: {
    padding: '5px 14px',
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '20px',
    color: '#f87171',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px 24px 80px',
  },
  statsRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '28px',
    flexWrap: 'wrap',
  },
  statCard: {
    background: '#0F1629',
    border: '1px solid #1E2D4A',
    borderRadius: '10px',
    padding: '14px 20px',
    minWidth: '80px',
  },
  statValue: {
    fontSize: '26px',
    fontWeight: 800,
    lineHeight: 1,
    marginBottom: '4px',
    fontFamily: "'Outfit', system-ui, sans-serif",
  },
  statLabel: {
    fontSize: '11px',
    color: '#4E6385',
    fontWeight: 600,
    fontFamily: "'Outfit', system-ui, sans-serif",
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  controls: {
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterPills: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  filterPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 12px',
    borderRadius: '99px',
    border: '1px solid #1E2D4A',
    background: 'transparent',
    color: '#6B7FA8',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: "'IBM Plex Sans', sans-serif",
    cursor: 'pointer',
    transition: 'all 0.15s',
    letterSpacing: '0.02em',
  },
  filterPillActive: {
    background: 'rgba(0, 229, 204, 0.12)',
    borderColor: 'rgba(0, 229, 204, 0.4)',
    color: '#00E5CC',
  },
  filterPillCount: {
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '99px',
    padding: '1px 6px',
    fontSize: '10px',
  },
  searchRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  searchWrap: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    color: '#4E6385',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    background: '#0F1629',
    border: '1px solid #1E2D4A',
    borderRadius: '8px',
    padding: '9px 36px 9px 36px',
    fontSize: '13px',
    color: '#E8EEFF',
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  searchClear: {
    position: 'absolute',
    right: '10px',
    background: 'transparent',
    border: 'none',
    color: '#4E6385',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
    padding: '2px 4px',
  },
  sortSelect: {
    background: '#0F1629',
    border: '1px solid #1E2D4A',
    borderRadius: '8px',
    padding: '9px 12px',
    fontSize: '13px',
    color: '#E8EEFF',
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none',
    cursor: 'pointer',
    flexShrink: 0,
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    gap: '12px',
    textAlign: 'center',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid rgba(0, 229, 204, 0.1)',
    borderTopColor: '#00E5CC',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '80px 24px',
    gap: '16px',
    textAlign: 'center',
    maxWidth: '460px',
    margin: '0 auto',
  },
  emptyIcon: {
    marginBottom: '8px',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#E8EEFF',
    margin: 0,
  },
  dimText: {
    fontSize: '13px',
    color: '#4E6385',
    margin: 0,
    lineHeight: 1.6,
  },
  errorBox: {
    background: 'rgba(239,68,68,0.06)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: '10px',
    padding: '20px 24px',
    color: '#f87171',
    marginBottom: '24px',
  },
  errorTitle: {
    fontSize: '14px',
    fontWeight: 700,
    marginBottom: '4px',
  },
  retryBtn: {
    marginTop: '12px',
    padding: '7px 16px',
    background: 'transparent',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '6px',
    color: '#f87171',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '11px 24px',
    background: 'linear-gradient(135deg, #2E6AFF 0%, #1a4fd8 100%)',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.03em',
  },
  ghostBtn: {
    padding: '9px 20px',
    background: 'transparent',
    border: '1px solid #1E2D4A',
    borderRadius: '8px',
    color: '#6B7FA8',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  card: {
    background: '#0F1629',
    border: '1px solid #1E2D4A',
    borderRadius: '12px',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    transition: 'border-color 0.2s',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    minHeight: '24px',
  },
  scoreWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  scoreLabel: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: '#4E6385',
    textTransform: 'uppercase',
  },
  cardBody: {
    flex: 1,
    minHeight: '44px',
  },
  jobTitle: {
    margin: '0 0 4px',
    fontSize: '15px',
    fontWeight: 700,
    color: '#E8EEFF',
    lineHeight: 1.3,
  },
  company: {
    margin: 0,
    fontSize: '12px',
    color: '#6B7FA8',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: '1px solid #1E2D4A',
    paddingTop: '12px',
    gap: '8px',
  },
  date: {
    fontSize: '11px',
    color: '#4E6385',
    letterSpacing: '0.03em',
    fontFamily: "'IBM Plex Mono', monospace",
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  loadBtn: {
    padding: '5px 14px',
    background: '#2E6AFF',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.03em',
  },
  deleteBtn: {
    padding: '5px 8px',
    background: 'transparent',
    border: '1px solid #1E2D4A',
    borderRadius: '6px',
    color: '#4E6385',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.15s',
  },
  confirmText: {
    fontSize: '12px',
    color: '#f87171',
    fontWeight: 600,
  },
  confirmYes: {
    padding: '4px 10px',
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '6px',
    color: '#f87171',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  confirmNo: {
    padding: '4px 10px',
    background: 'transparent',
    border: '1px solid #1E2D4A',
    borderRadius: '6px',
    color: '#6B7FA8',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
